import { z } from 'zod';
import { CareDocumentType, CaseReferenceSource } from '../../enums';
import { DurationSchema } from '../../DurationSchema';
import { requiresUnique, settingsEmailSchema } from './settingsValidation';

// Which care documents can be created for cases of an organization, and who
// they can be MANUALLY sent to. The `can*` toggles gate manual actions
// (buttons on the case documents table / resend paths) — nothing here sends
// automatically; naming follows referralPausing.canPause.
// Dependent fields keep their value while their toggle is off (retained,
// inert config) — except an ENABLED nurse flow, which must have an address.
// A present doc fully defines the group: all fields are required.
export const CareDocumentsSettingsSchema = z
  .strictObject({
    types: z.array(z.enum(CareDocumentType)).refine(...requiresUnique('types')),
    // which case identifier fills the "Case reference number" on generated
    // documents: L&G GIP code / Aviva policy number / none (field hidden) —
    // replaces the isLandgLikeOrganization/isAviva checks on the five
    // the care-document Create*Page forms of the web app (PPL-3294)
    caseReferenceSource: z.enum(CaseReferenceSource),
    // the "Care Forecast" section (expected care duration + anticipated cost
    // against delegated authority) — shown on the case's IAS form UI and
    // rendered on the generated document. Introduced for Aviva's
    // delegated-authority model (PPL-3295).
    careForecast: z.boolean(),
    // the "Up to N months" choices offered as the forecast's expected
    // duration (buildExpectedCareDurationOptions). Blank editor rows are
    // dropped by the API before validation. Required (non-empty) while the
    // careForecast toggle is on; kept (inert) when off.
    careForecastDurations: z
      .array(DurationSchema)
      .refine(
        ...requiresUnique<{ value: number; scale: string }>(
          'careForecastDurations',
          (duration) => `${duration.value}:${duration.scale}`,
        ),
      ),
    // the standalone "RTW not expected within N months" expected-duration
    // choice; null = the choice is not offered. Independent of the "Up to"
    // list; kept (inert) while the careForecast toggle is off.
    careForecastRtwNotExpectedWithin: DurationSchema.nullable(),
    // the "Send to case manager" button near every generated document
    canSendToCaseManager: z.boolean(),
    // CC recipients of the "document to case manager" email (the TO address
    // comes from the referral's case manager). Stored lowercase without
    // spaces — the API formats before validating (see settingsEmailSchema).
    caseManagerCcEmails: z
      .array(settingsEmailSchema)
      .refine(...requiresUnique('caseManagerCcEmails')),
    // document types whose "document to case manager" email additionally
    // CC's the member (the referral's patient email) — today Aviva's Return
    // to Work Plan only (PPL-3293). Kept (inert) while canSendToCaseManager
    // is off.
    caseManagerCcMemberTypes: z
      .array(z.enum(CareDocumentType))
      .refine(...requiresUnique('caseManagerCcMemberTypes')),
    // extra CC recipients of the "document to case manager" email applied
    // only to documents of the entry's type, on top of the org-wide
    // caseManagerCcEmails — today Aviva's Return to Work Plan → the insurer
    // reports inbox (PPL-3501). Entries may name a type outside `types`
    // (still honored on resends of existing documents of that type). The API
    // formats emails (lowercase, no spaces) and drops entries left with no
    // emails before validating, hence the min(1). Kept (inert) while
    // canSendToCaseManager is off.
    caseManagerCcEmailsByType: z
      .array(
        z.strictObject({
          type: z.enum(CareDocumentType),
          emails: z
            .array(settingsEmailSchema)
            .min(1, 'caseManagerCcEmailsByType entries must have emails')
            .refine(...requiresUnique('caseManagerCcEmailsByType emails')),
        }),
      )
      .refine(
        ...requiresUnique<{ type: CareDocumentType }>(
          'caseManagerCcEmailsByType',
          (entry) => entry.type,
        ),
      ),
    // manual sending of generated PCP copies to the organization's nurse
    // inbox via secure email (Paubox, PDF attached) — today only Healix
    // (replaces the EMAIL_HEALIX_NURSE env var)
    canSendToNurse: z.boolean(),
    // required while the nurse flow is enabled; kept (inert) when disabled.
    // '' is the empty value the editor stores when no address is set, so it
    // is accepted here and rejected by the cross-field rule below.
    nurseEmail: z.union([z.literal(''), settingsEmailSchema]),
  })
  .check((ctx) => {
    const { careForecast, careForecastDurations, canSendToNurse, nurseEmail } =
      ctx.value;

    if (careForecast && careForecastDurations.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['careForecastDurations'],
        message: 'careForecastDurations is required when careForecast is on',
      });
    }

    if (canSendToNurse && !nurseEmail) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['nurseEmail'],
        message: 'nurseEmail is required when canSendToNurse is on',
      });
    }
  });

export type CareDocumentsSettings = z.infer<typeof CareDocumentsSettingsSchema>;
